import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Plus, X, CreditCard, Clock } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentOptionsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [newMethod, setNewMethod] = useState("");
  const [newTerm, setNewTerm] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/organization`);
        if (response.data?.payment_options) {
          setPaymentMethods(response.data.payment_options.payment_methods || []);
          setPaymentTerms(response.data.payment_options.payment_terms || []);
        }
      } catch (err) {
        console.error("Error fetching settings:", err);
        toast.error("Failed to load payment options");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleAddMethod = () => {
    if (!newMethod.trim()) return;
    if (paymentMethods.includes(newMethod.trim())) {
      toast.error("This payment method already exists");
      return;
    }
    setPaymentMethods(prev => [...prev, newMethod.trim()]);
    setNewMethod("");
    setHasChanges(true);
  };

  const handleRemoveMethod = (method) => {
    setPaymentMethods(prev => prev.filter(m => m !== method));
    setHasChanges(true);
  };

  const handleAddTerm = () => {
    if (!newTerm.trim()) return;
    if (paymentTerms.includes(newTerm.trim())) {
      toast.error("This payment term already exists");
      return;
    }
    setPaymentTerms(prev => [...prev, newTerm.trim()]);
    setNewTerm("");
    setHasChanges(true);
  };

  const handleRemoveTerm = (term) => {
    setPaymentTerms(prev => prev.filter(t => t !== term));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings/organization`, {
        payment_options: {
          payment_methods: paymentMethods,
          payment_terms: paymentTerms
        }
      });
      setHasChanges(false);
      toast.success("Payment options saved successfully");
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error("Failed to save payment options");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-options-tab">
      {/* Payment Methods */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment Methods
          </CardTitle>
          <CardDescription>
            Manage the payment methods available for suppliers. These options appear in the dropdown when setting a supplier's default payment method.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current methods */}
          <div className="flex flex-wrap gap-2">
            {paymentMethods.map((method) => (
              <div
                key={method}
                className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-full text-sm"
              >
                <span>{method}</span>
                <button
                  onClick={() => handleRemoveMethod(method)}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  data-testid={`remove-method-${method}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {paymentMethods.length === 0 && (
              <p className="text-sm text-muted-foreground">No payment methods configured</p>
            )}
          </div>

          {/* Add new method */}
          <div className="flex gap-2">
            <Input
              value={newMethod}
              onChange={(e) => setNewMethod(e.target.value)}
              placeholder="Add new payment method..."
              className="max-w-xs"
              onKeyDown={(e) => e.key === "Enter" && handleAddMethod()}
              data-testid="new-method-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddMethod}
              disabled={!newMethod.trim()}
              data-testid="add-method-button"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payment Terms */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Payment Terms
          </CardTitle>
          <CardDescription>
            Manage the payment terms available for suppliers and customers. These options appear in the dropdown when setting default payment terms.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current terms */}
          <div className="flex flex-wrap gap-2">
            {paymentTerms.map((term) => (
              <div
                key={term}
                className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-full text-sm"
              >
                <span>{term}</span>
                <button
                  onClick={() => handleRemoveTerm(term)}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  data-testid={`remove-term-${term}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {paymentTerms.length === 0 && (
              <p className="text-sm text-muted-foreground">No payment terms configured</p>
            )}
          </div>

          {/* Add new term */}
          <div className="flex gap-2">
            <Input
              value={newTerm}
              onChange={(e) => setNewTerm(e.target.value)}
              placeholder="Add new payment term (e.g., 120 dagen)..."
              className="max-w-xs"
              onKeyDown={(e) => e.key === "Enter" && handleAddTerm()}
              data-testid="new-term-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddTerm}
              disabled={!newTerm.trim()}
              data-testid="add-term-button"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Save button */}
      {hasChanges && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-payment-options"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PaymentOptionsTab;
